import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react-lite";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../pages/Header";
import { PODBORKI_IMAGE_BASE_URL } from "../api/podborki";
import podborkiStore from "../stores/podborkiStore";
import PortalNavigation from "./PortalNavigation";

const PodborkaEdit: React.FC = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const podborkaId = Number(id);
  const podborka = podborkiStore.selectedPodborka;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sort, setSort] = useState(0);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [pendingMovieIds, setPendingMovieIds] = useState<number[]>([]);

  useEffect(() => {
    if (Number.isInteger(podborkaId) && podborkaId > 0) {
      podborkiStore.fetchPodborkaDetails(podborkaId);
    }
  }, [podborkaId]);

  useEffect(() => {
    if (!podborka) return;
    setName(podborka.name);
    setDescription(podborka.description ?? "");
    setSort(podborka.sort);
  }, [podborka]);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = () => {
    window.alert("Сохранение будет подключено после получения API");
  };

  const handleRemoveMovie = async (movieId: number) => {
    if (pendingMovieIds.includes(movieId)) return;
    setPendingMovieIds((previous) => [...previous, movieId]);

    const changed = await podborkiStore.changeFilmPodborka(movieId, podborkaId, 0);
    if (changed) await podborkiStore.fetchPodborkaDetails(podborkaId);

    setPendingMovieIds((previous) => previous.filter((id) => id !== movieId));
  };

  const handleMoveMovie = async (movieId: number) => {
    if (pendingMovieIds.includes(movieId)) return;
    setPendingMovieIds((previous) => [...previous, movieId]);

    const removed = await podborkiStore.changeFilmPodborka(movieId, podborkaId, 0);
    const added = removed
      ? await podborkiStore.changeFilmPodborka(movieId, podborkaId, 1)
      : false;

    if (added) await podborkiStore.fetchPodborkaDetails(podborkaId);
    setPendingMovieIds((previous) => previous.filter((id) => id !== movieId));
  };

  if (!Number.isInteger(podborkaId) || podborkaId <= 0) {
    return <Alert severity="error">Некорректный ID подборки</Alert>;
  }

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column">
      <Header />
      <Box sx={{ flexGrow: 1, p: 2 }}>
        <PortalNavigation />

        <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto" }}>
          {podborkiStore.detailsLoading && (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          )}

          {podborkiStore.detailsError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={() => podborkiStore.fetchPodborkaDetails(podborkaId)}>
                  Повторить
                </Button>
              }
            >
              {podborkiStore.detailsError}
            </Alert>
          )}

          {!podborkiStore.detailsLoading && podborka && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "300px minmax(0, 1fr)" },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                <Box
                  component="img"
                  src={imagePreview ?? `${PODBORKI_IMAGE_BASE_URL}/${podborka.id}.jpg`}
                  alt={podborka.name}
                  sx={{ width: "100%", height: 170, objectFit: "cover", borderRadius: 1 }}
                />

                <Box>
                  <Button component="label" variant="outlined">
                    Загрузить картинку
                    <input hidden type="file" accept="image/*" onChange={handleImageChange} />
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Новая картинка отображается только локально до подключения API сохранения.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 180px" },
                  gap: 2,
                  mt: 3,
                }}
              >
                <TextField label="Название подборки" value={name} onChange={(e) => setName(e.target.value)} />
                <TextField label="Описание" value={description} onChange={(e) => setDescription(e.target.value)} />
                <TextField
                  label="Сортировка"
                  type="number"
                  value={sort}
                  onChange={(e) => setSort(Number(e.target.value))}
                />
              </Box>

              <Box display="flex" gap={2} mt={3}>
                <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave}>
                  Сохранить изменения
                </Button>
                <Button variant="outlined" color="inherit" onClick={() => navigate("/collections")}>
                  Отмена
                </Button>
              </Box>

              <Typography variant="h5" fontWeight={700} sx={{ mt: 5, mb: 2 }}>
                Фильмы ({podborkiStore.movies.length})
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 1,
                }}
              >
                {podborkiStore.movies.map((movie) => (
                  <Card key={movie.id} variant="outlined" sx={{ display: "flex", minHeight: 124 }}>
                    <CardMedia
                      component="img"
                      image={movie.logo}
                      alt={movie.title}
                      sx={{ width: 82, objectFit: "cover" }}
                    />
                    <CardContent
                      sx={{
                        p: 1.5,
                        flexGrow: 1,
                        minWidth: 0,
                        "&:last-child": { pb: 1.5 },
                      }}
                    >
                      <Typography
                        component={Link}
                        to={`/filmdata?id=${movie.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontWeight={700}
                        sx={{
                          color: "text.primary",
                          textDecoration: "none",
                          "&:hover": { color: "primary.main", textDecoration: "underline" },
                        }}
                      >
                        {movie.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {movie.year}
                      </Typography>
                    </CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        p: 0.5,
                      }}
                    >
                      <Tooltip title="Переместить фильм выше">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={pendingMovieIds.includes(movie.id)}
                            onClick={() => handleMoveMovie(movie.id)}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Удалить из подборки">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={pendingMovieIds.includes(movie.id)}
                            onClick={() => handleRemoveMovie(movie.id)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Card>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default PodborkaEdit;
